import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';

import { useSitesSso } from '@newfold/huapi-js';

import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useHostingInfo from '~/hooks/useHostingInfo';
import { ButtonVariantPropTypes } from '~/types/mui';

import useAlerts from '../../Alerts/alertsStore';

type SiteSsoLinkPropOptions = {
  ssoQueryParams?: string;
  variant?: ButtonVariantPropTypes | 'menuItem';
  userId?: number;
  onAction?: VoidFunction;
  id?: string | number;
};

const SiteSsoLink = ({
  children = undefined,
  ssoQueryParams = '',
  variant = 'outlined',
  onAction = undefined,
  id = undefined,
  ...props
}: PropsWithChildren<SiteSsoLinkPropOptions>) => {
  const { t } = useTranslation('site');
  const [, { generateAlert }] = useAlerts();
  const { siteId: paramsSiteId } = useParams();
  const siteId = Number(id || paramsSiteId);

  const [isPending, setIsPending] = useState(false);

  const { isDisabled } = useHostingInfo();

  const { brandFromJWT } = useBrandInfo();

  const brand = brandFromJWT.toLowerCase();

  const hookOptions = {
    query: {
      refetchOnWindowFocus: false,
      enabled: false,
    },
  };

  //site sso
  const { refetch } = useSitesSso(siteId, {}, hookOptions);

  const redirectToSsoUrl = async () => {
    const wordpressSsoLoadingPage =
      brand === 'bluehost'
        ? `${process.env.PUBLIC_URL}/loading/wordpress-${brand}.html`
        : `${process.env.PUBLIC_URL}/loading/wordpress.html`;

    // Actually open the window
    var ssoWindow = window.open(wordpressSsoLoadingPage, '_blank');
    setIsPending(true);

    const { data, error } = await refetch();

    if (error) {
      // close the window
      ssoWindow?.close();
      setIsPending(false);
      return generateAlert({
        severity: 'error',
        description: t('error.alertError'),
        showCloseBtn: true,
      });
    }
    const url = data?.data?.sso;

    if (url && !!ssoWindow) {
      // set url for window
      ssoWindow.location = url + ssoQueryParams;

      setIsPending(false);
      if (onAction) {
        onAction();
      }
      return;
    }

    ssoWindow?.close();

    // TODO: need some copy from UX
    generateAlert({
      severity: 'error',
      description: t('ssoLink.notFound'),
      showCloseBtn: true,
    });
    setIsPending(false);
  };

  return variant === 'menuItem' ? (
    <MenuItem
      onClick={redirectToSsoUrl}
      disabled={isDisabled}
      {...props}
      data-testid={'sitessolink-menuitem'}
    >
      {isPending ? <CircularProgress size={20} /> : children}
    </MenuItem>
  ) : (
    <LoadingButtonWrapper
      loading={isPending}
      variant={variant}
      color="primary"
      onClick={redirectToSsoUrl}
      disabled={isDisabled}
      data-testid={'sitessolink-button'}
      {...props}
    >
      {children}
    </LoadingButtonWrapper>
  );
};

export default SiteSsoLink;
